import React, { useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { Button } from '@interco/inter-ui/components/Button'
import { Content } from '@organisms/SemEmprestimo/styles'
import { ColorWrapper, TextParagraph, GridStyled } from '@atoms'
import { Page } from '@templates'
import { ApplicationState } from '@store/types'
import { validaEnvHomeByBridge } from '@utils/functions'
import { BridgeService } from '@services/bridge'
import Tag from '@utils/Tag'
import { setSolicitarContratacaoOrigin } from '@store/refinanciamento/simulacao/actions'
import { ErrorIcon } from '@signals'
import { ConvenioKind, getConvenioKind, StepRefin } from '@utils/enums'
import useTrackingRefin from '@hooks/useTrackingRefin'
import { clearError } from '@store/ui/error/actions'

const ERRO_CARREGAMENTO_DADOS_1: Record<ConvenioKind, StepRefin> = {
  INSS: StepRefin.ERRO_CARREGAMENTO_INSS_DADOS_1,
  SIAPE: StepRefin.ERRO_CARREGAMENTO_SIAPE_DADOS_1,
  CTPS: StepRefin.ERRO_CARREGAMENTO_CTPS_DADOS_1,
}

const ERRO_CARREGAMENTO_DADOS_2: Record<ConvenioKind, StepRefin> = {
  INSS: StepRefin.ERRO_CARREGAMENTO_INSS_DADOS_2,
  SIAPE: StepRefin.ERRO_CARREGAMENTO_SIAPE_DADOS_2,
  CTPS: StepRefin.ERRO_CARREGAMENTO_CTPS_DADOS_2,
}

const ERRO_STEP_BY_STAGE: Record<string, StepRefin> = {
  'convenio-indisponivel': StepRefin.ERRO_CONVENIO_INDISPONIVEL,
  'erro-buscar-proposta': StepRefin.ERRO_BUSCAR_PROPOSTA,
  'listagem-contratos': StepRefin.LISTAGEM_CONTRATOS,
  'erro-contratacao-proposta': StepRefin.ERRO_CONTRATACAO_PROPOSTA,
  'iniciar-simular': StepRefin.ERRO_CARREGAMENTO_SIAPE_SIMULAR,
}

const Erro = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isApp, origemGoBack } = useSelector((state: ApplicationState) => state.ui.navigation)
  const { step } = useLocation().state || {}
  const { convenioCodigo } = useSelector(
    (state: ApplicationState) => state.refinanciamento.contratos,
  )

  function getStep() {
    const convenioKind = getConvenioKind(convenioCodigo)

    if (step === 'carregamento-dados-1') {
      return ERRO_CARREGAMENTO_DADOS_1[convenioKind]
    }
    if (step === 'carregamento-dados-2') {
      return ERRO_CARREGAMENTO_DADOS_2[convenioKind]
    }

    return ERRO_STEP_BY_STAGE[step] ?? StepRefin.ERRO
  }

  const getStepRefin = getStep()
  useTrackingRefin(getStepRefin)

  useEffect(() => {
    BridgeService.tac({
      name: 'erro',
      actions: ['tentar_novamente', 'voltar_para_home', 'btn_continuar_refinanciamento'],
    })

    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:feedback',
        ref_type: '',
        content_action: 'Dado carregado',
        action_id: 'Encontramos um erro',
        section: 'feedback',
        sub_section: '',
        step: '',
        scenario: 'error',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'page_loaded',
        element_name: 'Encontramos um erro',
        c_page: window.location.href,
        redirect_url: '',
        section_name: 'payrollloan:refin:feedback',
        section: 'feedback',
        sub_section: '',
        step: '',
        scenario: 'error',
      })
    }
  }, [isApp])

  return (
    <Page
      stickyContainerFooterBottom="30px"
      showLeftIcon={false}
      positionFooter="sticky"
      stickyFooter={
        <>
          <Button
            variant="primary"
            onClick={() => {
              BridgeService.tic({
                name: 'btn_tentar_novamente',
                action: 'tentar_novamente',
              })

              if (isApp) {
                Tag.enviarTagueamentoApp({
                  screen: 'clt:refin:feedback',
                  ref_type: 'botao',
                  content_action: 'Toque',
                  action_id: 'Tentar novamente',
                  section: 'feedback',
                  sub_section: '',
                  step: '',
                  scenario: 'error',
                  ref_figma: '2',
                })
              } else {
                Tag.enviarTagueamentoWeb({
                  element_action: 'click button',
                  element_name: 'Tentar novamente',
                  c_page: window.location.href,
                  redirect_url: '',
                  section_name: 'payrollloan:refin:feedback',
                  section: 'feedback',
                  sub_section: '',
                  step: '',
                  scenario: 'error',
                })
              }

              dispatch(clearError())
              dispatch(setSolicitarContratacaoOrigin(true))
              navigate(-1)
            }}
          >
            Tentar novamente
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              BridgeService.tic({
                name: 'btn_voltar_home',
                action: 'voltar_para_home',
              })

              if (isApp) {
                Tag.enviarTagueamentoApp({
                  screen: 'clt:refin:feedback',
                  ref_type: 'botao',
                  content_action: 'Toque',
                  action_id: 'Voltar para home',
                  section: 'feedback',
                  sub_section: '',
                  step: '',
                  scenario: 'error',
                  ref_figma: '2',
                })
              } else {
                Tag.enviarTagueamentoWeb({
                  element_action: 'click button',
                  element_name: 'Voltar para home',
                  c_page: window.location.href,
                  redirect_url: '',
                  section_name: 'payrollloan:refin:feedback',
                  section: 'feedback',
                  sub_section: '',
                  step: '',
                  scenario: 'error',
                })
              }

              window.location.href = validaEnvHomeByBridge(String(origemGoBack), isApp) || ''
            }}
          >
            Voltar para home
          </Button>
        </>
      }
    >
      <Content>
        <GridStyled margin="0">
          <ErrorIcon />
        </GridStyled>
        <ColorWrapper fontWeight="600" margin="24px 0 8px 0" style={{ textAlign: 'center' }}>
          <TextParagraph variant="headline-h1">Encontramos um erro</TextParagraph>
        </ColorWrapper>
        <ColorWrapper margin="0 0 8px 0" style={{ textAlign: 'center' }}>
          <TextParagraph variant="body-3">
            Mas fique tranquilo, estamos resolvendo e logo mais você poderá dar continuidade no
            processo de contratação.
          </TextParagraph>
        </ColorWrapper>
        <ColorWrapper margin="0 0 16px 0" style={{ textAlign: 'center' }}>
          <TextParagraph variant="body-3">
            Retorne após algum tempo ou clique em &quot;Tentar novamente&quot;.
          </TextParagraph>
        </ColorWrapper>
      </Content>
    </Page>
  )
}

export default Erro
